import { useContext, useEffect, useState, type ChangeEvent, type MouseEvent } from 'react';
import { Tooltip } from 'bootstrap';
import { SmartContext } from '../../../library/Core/SmartContext';
import { DomainElement } from '../../../library/Core/SmartTypes';
import { getActiveShowProfileFiltersFromConfig, getDomainValueForCode, isSchoolAdmin, resolveDirectoryFilterItemClass } from '../../../library/Core/SmartFunctions';
import EntrepreneurDirectoryService from './EntrepreneurDirectoryService';
// import { REVENUE_SORT_HIGH_TO_LOW, REVENUE_SORT_OPTIONS } from './EntrepreneurDirectoryService';
import FactLoader from '../../../library/SimpleControls/FactLoader';
import DirectoryFilterDomainCheckboxes, { DirectoryShowProfileSection } from '../shared/DirectoryFilterDomainCheckboxes';

export const handleClearAllFilterClick = (event: MouseEvent<HTMLElement>, state: any, dispatch: any) => {
    event.preventDefault();
    const criteria = EntrepreneurDirectoryService.getDefaultSearchCriteria();
    dispatch({ type: 'CONTROL_VALUE_CHANGE', payload: { dataKey: 'filters', value: criteria } });
    // Signal EntrepreneurSearchControl to clear per-column filter search text
    dispatch({
        type: 'CONTROL_VALUE_CHANGE',
        payload: { dataKey: 'filterSearchTermsResetToken', value: Date.now() },
    });
    EntrepreneurDirectoryService.performSearchWithUserSelectedCriteria(criteria, state, dispatch, { selectedCard: '' });
};

interface ExpandedControls {
    [key: string]: boolean;
}
type YearDomain = { code: string | number; value: string };

const makeDecadeBuckets = (domains: YearDomain[], foundingYear: number = 1950) => {
    if (!domains?.length) return { beforeLabel: '', beforeYears: [] as YearDomain[], groups: [] as any[] };

    const currentYear = new Date().getFullYear();
    const nums = domains.map((d) => parseInt(String(d.code), 10)).filter(Number.isFinite).sort((a, b) => a - b);
    if (!nums.length) return { beforeLabel: '', beforeYears: [], groups: [] };

    const minYear = nums[0];
    const maxYear = nums[nums.length - 1];
    const startDecade = Math.floor(Math.max(foundingYear, minYear) / 10) * 10;
    const endDecade = Math.floor(maxYear / 10) * 10;

    const beforeYears = domains
        .filter((d) => parseInt(String(d.code), 10) < startDecade)
        .sort((a, b) => parseInt(String(a.code), 10) - parseInt(String(b.code), 10));

    const groups: any[] = [];
    for (let d = startDecade; d <= endDecade; d += 10) {
        const label = d === endDecade ? `${d}-${currentYear}` : `${d}-${d + 9}`;
        const years = domains
            .filter((x) => {
                const y = parseInt(String(x.code), 10);
                return y >= d && y <= (d === endDecade ? currentYear : d + 9);
            })
            .sort((a, b) => parseInt(String(a.code), 10) - parseInt(String(b.code), 10));
        if (years.length) groups.push({ from: d, to: d === endDecade ? currentYear : d + 9, label, years });
    }

    return {
        beforeLabel: beforeYears.length ? `Before ${startDecade}` : '',
        beforeYears,
        groups,
    };
};

const EntrepreneurSearchControl = () => {
    const uniqueCodes = new Set<string>();
    const { state, dispatch } = useContext(SmartContext);

    const [performSearch, setPerformSearch] = useState(0);
    const [loading, setLoading] = useState(false);
    const [searchTerms, setSearchTerms] = useState({} as any);
    const filterSearchTermsResetToken = state?.data?.filterSearchTermsResetToken;
    useEffect(() => {
        if (filterSearchTermsResetToken) {
            setSearchTerms({});
        }
    }, [filterSearchTermsResetToken]);
    const [showFilters, setShowFilters] = useState(true);
    const [expandedControls, setExpandedControls] = useState<ExpandedControls>({});
    const [expandedDecades, setExpandedDecades] = useState<Record<string, boolean>>({});
    const [isOpen, setOpen] = useState(false);

    const searchFilterDomains = (state as any).data?.searchDomain || {};
    const isAdmin = isSchoolAdmin();
    const filterConfigWithDomain = EntrepreneurDirectoryService.withDomainShowProfileLabels(state);
    const filters = Object.values(filterConfigWithDomain).map((c: any) =>
        c.applicableFor === 'ADMIN' ? { ...c, isVisible: isAdmin } : c
    );
    const hasPresentOptions = (control: any) =>
        Array.isArray(searchFilterDomains[control.id]) && searchFilterDomains[control.id].length > 0;
    const mainFilters = filters.filter(
        (c: any) => !c.isAdditional && c.isVisible && !c.isShowProfileFilter && hasPresentOptions(c)
    );
    const firstRowFilters = mainFilters.slice(0, 4);
    const secondRowFilters = mainFilters.slice(4);
    const foundingYear =
        Number(
            (state as any).data?.searchDomain?.foundingYear ||
                (state as any).internal?.searchFilterDomains?.foundingYear
        ) || 1950;

    useEffect(() => {
        const els = [].slice.call(document.querySelectorAll('.defaultTooltip'));
        const tips = els.map((el: any) => new Tooltip(el));
        return () => tips.forEach((t: any) => t.dispose());
    });

    useEffect(() => {
        let active = true;
        setLoading(true);
        const criteria =
            performSearch === 0
                ? { ...EntrepreneurDirectoryService.getDefaultSearchCriteria(), ...(state as any).data?.filters }
                : (state as any).data?.filters;

        EntrepreneurDirectoryService.performSearchWithUserSelectedCriteria(criteria, state, dispatch).finally(() => {
            if (active) setLoading(false);
        });
        return () => {
            active = false;
        };
    }, [performSearch]);

    const updateFilters = (type: string, dataKey: string, value: any) => {
        dispatch({ type, payload: { dataKey, value, errorMessages: [] } });
        setPerformSearch((prev) => prev + 1);
    };

    const clearChildFilters = (parentId: string) => {
        Object.values(EntrepreneurDirectoryService.filterConfig)
            .filter((cfg: any) => cfg.parentId === parentId)
            .forEach((child: any) => {
                const currentValues = (state as any).data?.filters?.[child.id] || [];
                if (currentValues.length > 0) {
                    dispatch({
                        type: 'CONTROL_VALUE_CHANGE',
                        payload: { dataKey: `filters.${child.id}`, value: [], errorMessages: [] },
                    });
                }
            });
    };

    const handleSearchListItemSelect = (event: ChangeEvent<HTMLInputElement>) => {
        const { id, value, checked } = event.target;
        const type = checked ? 'CHECKBOX_ADD_NEW_RECORD_IN_ARRAY' : 'DELETE_RECORD_IN_SIMPLE_ARRAY';
        clearChildFilters(id);
        updateFilters(type, `filters.${id}`, value);
    };

    const handleBooleanFilterChange = (filterKey: string) => (event: ChangeEvent<HTMLInputElement>) => {
        const checkedValue = EntrepreneurDirectoryService.filterConfig[filterKey]?.booleanCheckedValue ?? true;
        updateFilters('CONTROL_VALUE_CHANGE', `filters.${filterKey}`, event.target.checked ? checkedValue : null);
    };

    const handleAppliedFilterClick = (event: any, key: string, value: string) => {
        event.preventDefault();
        clearChildFilters(key);
        updateFilters('DELETE_RECORD_IN_SIMPLE_ARRAY', `filters.${key}`, value);
    };

    const handleBooleanFilterClear = (event: MouseEvent, key: string) => {
        event.preventDefault();
        updateFilters('CONTROL_VALUE_CHANGE', `filters.${key}`, null);
    };

    // const selectedRevenueSort = (state as any).data?.filters?.revenueSort || REVENUE_SORT_HIGH_TO_LOW;
    // const selectedRevenueBand = selectedRevenueSort;
    // const handleRevenueSortChange = (event: ChangeEvent<HTMLSelectElement>) => {
    //     updateFilters('CONTROL_VALUE_CHANGE', 'filters.revenueSort', event.target.value);
    // };

    const getFilteredDomainItems = (control: any) => {
        let items = searchFilterDomains[control.id] || [];
        if (control.parentIdApply && control.parentId) {
            const parentValues = (state as any).data?.filters?.[control.parentId] || [];
            if (!parentValues.length) {
                return {
                    items: [],
                    needsParent: true,
                    parentLabel: EntrepreneurDirectoryService.filterConfig[control.parentId]?.label || control.parentId,
                };
            }
            items = items.filter((d: any) => {
                if (d.parentCode) return parentValues.map(String).includes(String(d.parentCode));
                return true;
            });
        }
        return { items, needsParent: false, parentLabel: '' };
    };

    const getSelectedPassOutYears = () => ((state as any).data?.filters?.passOutYear || []).map(String);

    const isYearGroupFullySelected = (years: YearDomain[]) => {
        const selected = getSelectedPassOutYears();
        return years.length > 0 && years.every((y) => selected.includes(String(y.code)));
    };

    const isYearGroupPartiallySelected = (years: YearDomain[]) => {
        const selected = getSelectedPassOutYears();
        const selectedCount = years.filter((y) => selected.includes(String(y.code))).length;
        return selectedCount > 0 && selectedCount < years.length;
    };

    const handleYearGroupSelectAll = (label: string, years: YearDomain[]) => (event: ChangeEvent<HTMLInputElement>) => {
        event.stopPropagation();
        const yearCodes = years.map((y) => String(y.code));
        const selected = getSelectedPassOutYears();
        const allSelected = yearCodes.every((c) => selected.includes(c));
        const next = allSelected
            ? selected.filter((c: string) => !yearCodes.includes(c))
            : [...new Set([...selected, ...yearCodes])];
        setExpandedDecades((prev) => ({ ...prev, [label]: !allSelected }));
        updateFilters('CONTROL_VALUE_CHANGE', 'filters.passOutYear', next);
    };

    const yearCheckbox = (yd: YearDomain) => {
        const codeStr = String(yd.code);
        const checked = getSelectedPassOutYears().includes(codeStr);
        return (
            <div key={codeStr} className="form-check d-flex flex-wrap ms-1">
                <input
                    id="passOutYear"
                    className="form-check-input me-2"
                    type="checkbox"
                    value={yd.code}
                    name="passOutYear_Name"
                    checked={checked}
                    onChange={handleSearchListItemSelect}
                    onClick={(e) => e.stopPropagation()}
                />
                <label className="form-check-label font-16 font-400 flex-1 pt-1" onClick={(e) => e.stopPropagation()}>
                    {yd.value}
                </label>
            </div>
        );
    };

    const renderYearGroupRow = (label: string, years: YearDomain[]) => {
        const open = !!expandedDecades[label];
        const allSelected = isYearGroupFullySelected(years);
        const partiallySelected = isYearGroupPartiallySelected(years);
        return (
            <div key={label} className="mb-2">
                <div
                    className={`form-check d-flex flex-wrap align-items-center parent ${open ? '' : 'collapsed'}`}
                    role="button"
                    aria-expanded={open}
                    onClick={() => setExpandedDecades((prev) => ({ ...prev, [label]: !prev[label] }))}>
                    <input
                        className="form-check-input me-2"
                        type="checkbox"
                        checked={allSelected}
                        ref={(el) => {
                            if (el) el.indeterminate = partiallySelected;
                        }}
                        onChange={handleYearGroupSelectAll(label, years)}
                        onClick={(e) => e.stopPropagation()}
                        aria-label={`Select all ${label}`}
                    />
                    <label className="form-check-label font-16 font-400 flex-1 pt-1">{label}</label>
                </div>
                {open && <div className="child ps-4 show">{years.map(yearCheckbox)}</div>}
            </div>
        );
    };

    const renderFilterItems = (controlList: any[]) => {
        const visibleControls = controlList.filter((control) => control.isVisible);
        return visibleControls.map((control: any, index: number) => {
            const isExpanded = !isOpen ? true : index === 0 || !!expandedControls[control.id];
            return (
                <div key={control.id} className={resolveDirectoryFilterItemClass(control.class, visibleControls.length)}>
                    <div className="filterHeader">
                        <div className="d-flex justify-content-between align-items-center position-relative">
                            <button
                                className="btn-nostyle text-start flex-1 d-flex justify-content-between align-items-center"
                                type="button"
                                onClick={() => setExpandedControls((prev) => ({ [control.id]: !prev[control.id] }))}
                                data-bs-toggle="collapse"
                                data-bs-target={`#collapse${control.id}`}
                                aria-expanded={isExpanded ? 'true' : 'false'}>
                                <span className="font-18 font-600">{control.label}</span>
                                <i className="bi bi-info-circle-fill defaultTooltip user-select-all" data-bs-toggle="tooltip" title={control.tooltip} />
                            </button>
                        </div>
                    </div>
                    <div id={`collapse${control.id}`} className={`accordion-collapse collapse ${isExpanded ? 'show' : ''}`}>
                        {control.hasSearch && (
                            <div className="filterSearchblk">
                                <form className="d-flex align-items-center filterSearch position-relative">
                                    <input
                                        className="form-control flex-1 font-14 font-500 w-100"
                                        type="search"
                                        placeholder="Search"
                                        value={searchTerms[control.id] || ''}
                                        onChange={(e) => setSearchTerms((prev: any) => ({ ...prev, [control.id]: e.target.value }))}
                                    />
                                    <button className="btn icon-search" type="button" />
                                </form>
                            </div>
                        )}
                        <div className="card card-body">
                            <div className="overflow-hidden">
                                <div className="scrollMe">
                                    {control.id === 'passOutYear' ? (
                                        (() => {
                                            const src: YearDomain[] = searchFilterDomains.passOutYear ?? [];
                                            const q = (searchTerms.passOutYear || '').toLowerCase();
                                            const filtered = !q
                                                ? src
                                                : src.filter(
                                                      (d) =>
                                                          String(d.value).toLowerCase().includes(q) ||
                                                          String(d.code).toLowerCase().includes(q)
                                                  );
                                            const { beforeLabel, beforeYears, groups } = makeDecadeBuckets(filtered, foundingYear);
                                            return (
                                                <>
                                                    {beforeLabel && beforeYears.length > 0 && renderYearGroupRow(beforeLabel, beforeYears)}
                                                    {groups.map((g) => renderYearGroupRow(g.label, g.years))}
                                                </>
                                            );
                                        })()
                                    ) : (
                                        (() => {
                                            const { items: domainItems, needsParent, parentLabel } = getFilteredDomainItems(control);
                                            if (needsParent) {
                                                return (
                                                    <div className="text-muted font-14 p-2">
                                                        Select <b>{parentLabel}</b> first
                                                    </div>
                                                );
                                            }
                                            return (
                                                <DirectoryFilterDomainCheckboxes
                                                    controlId={control.id}
                                                    domainCategory={control.domain}
                                                    controlConfig={control}
                                                    items={domainItems}
                                                    searchTerm={searchTerms[control.id] || ''}
                                                    dedupeCodes={control.id === 'programId' ? uniqueCodes : undefined}
                                                    isChecked={(code) => {
                                                        const selected = ((state as any).data?.filters?.[control.id] || []).map(String);
                                                        if (selected.includes(String(code))) return true;
                                                        if (control.id !== 'revenueBand') return false;
                                                        const needle = EntrepreneurDirectoryService.normalizeRevenueToken(String(code));
                                                        return selected.some(
                                                            (v: string) =>
                                                                EntrepreneurDirectoryService.normalizeRevenueToken(v) === needle
                                                        );
                                                    }}
                                                    onChange={handleSearchListItemSelect}
                                                />
                                            );
                                        })()
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            );
        });
    };

    let hasAppliedFilters = false;
    const appliedFilterChips = Object.keys((state as any).data?.filters || {}).flatMap((key) => {
        const val = (state as any).data?.filters[key];
        if (!Array.isArray(val)) return [];
        return val.map((v: string) => {
            const cfg = filterConfigWithDomain[key];
            if (!cfg) return null;
            if (cfg.applicableFor === 'ADMIN' && !isAdmin) return null;
            const displayValue = cfg.domain
                ? getDomainValueForCode(v, cfg.domain, state) || v
                : (searchFilterDomains[key]?.find((d: DomainElement) => String(d.code) === String(v))?.value ?? v);
            hasAppliedFilters = true;
            return (
                <li key={`${key}_${v}`}>
                    <a href="javascript:;" onClick={(e) => handleAppliedFilterClick(e, key, v)}>
                        {cfg.label}: <b>{displayValue}</b> <i className="icon-cross"></i>
                    </a>
                </li>
            );
        });
    });
    const showProfileFilterChips = getActiveShowProfileFiltersFromConfig(
        (state as any).data?.filters,
        filterConfigWithDomain
    ).map(({ key, label }) => {
        hasAppliedFilters = true;
        return (
            <li key={`show-profile-${key}`}>
                <a href="javascript:;" onClick={(e) => handleBooleanFilterClear(e, key)}>
                    {label} <i className="icon-cross"></i>
                </a>
            </li>
        );
    });

    return (
        <>
            <FactLoader show={loading} />
            <section className="alumni_directory_filter container-fluid">
                <div className="max-1140 mx-auto">
                    <div className="JobInternshipListing">
                        <div className="ji_filtersec aos-init aos-animate">
                            <div className="mobileFilter d-flex justify-content-between align-items-center d-md-none mobileonly text-end">
                                <button className="btn-filter btn-unscroll font-18 font-500 w-100" type="button" onClick={() => setOpen(!isOpen)}>
                                    <i className="icon-filter"></i>
                                    <span>Filters</span>
                                </button>
                            </div>

                            <div className={`accordion collapse show directory_wrap ${isOpen ? 'open' : ''}`} id="filter_collapse">
                                <div className="filters-head">
                                    <div className="filters-head-left">
                                        <h3
                                            className="font-18 font-500 mb-0"
                                            role="button"
                                            onClick={() => setShowFilters(!showFilters)}
                                            data-bs-toggle="collapse"
                                            data-bs-target="#accordionFilters"
                                            aria-expanded={showFilters ? 'true' : 'false'}>
                                            <span className="icon-filter"></span>
                                            <span>Filters</span>
                                        </h3>
                                        <div
                                            className={`badge rounded-pill text-bg-secondary py-2 px-3 bg-opacity-25 text-dark mt-3 mt-md-0  ${(state as any)?.flags?.isDataLoading ? ' is-loading' : ''}`}
                                            id="UsersCountsTop">
                                            <div className="directory-result-count__body">
                                                {(state as any)?.flags?.isDataLoading ? (
                                                    <span className="directory-result-count__label">Searching…</span>
                                                ) : (
                                                    <span className="directory-result-count__row">
                                                        <span className="directory-result-count__num font-14 font-500">
                                                            {((state as any)?.data?.totalCount ?? (state as any)?.data?.students?.length ?? 0).toLocaleString()}
                                                        </span>
                                                        <span className="directory-result-count__label font-14 font-400">
                                                            {(state as any)?.data?.selectedCard === 'uniqueCompanies'
                                                                ? ' companies found'
                                                                : ' entrepreneurs found'}
                                                        </span>
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <a className="btn-clear btn-tab-clear d-block d-md-none" role="button" onClick={(e) => handleClearAllFilterClick(e, state, dispatch)}>
                                        Clear
                                    </a>
                                    {/* Revenue sort dropdown — high to low / low to high. Re-enable after review.
                                    {isAdmin && (
                                        <div className="filters-head-right">
                                            <label className="ent-dir-revenue-filter" htmlFor="entrepreneurRevenueSort">
                                                <span className="ent-dir-revenue-filter__label">Revenue</span>
                                                <select
                                                    id="entrepreneurRevenueSort"
                                                    className="form-select"
                                                    value={selectedRevenueBand}
                                                    onChange={handleRevenueSortChange}
                                                    title="Sort by company revenue">
                                                    {REVENUE_SORT_OPTIONS.map((opt) => (
                                                        <option key={opt.code} value={opt.code}>
                                                            {opt.value}
                                                        </option>
                                                    ))}
                                                </select>
                                            </label>
                                        </div>
                                    )}
                                    */}
                                </div>

                                {showFilters && (
                                    <div className="accordion-collapse collapse show" id="accordionFilters">
                                        <div className="filterGroup mt-3">{renderFilterItems(firstRowFilters)}</div>
                                        <div className="filterGroup">
                                            {renderFilterItems(secondRowFilters)}
                                            <DirectoryShowProfileSection
                                                title="Show Profiles"
                                                filterConfig={filterConfigWithDomain}
                                                filterValues={(state as any).data?.filters}
                                                onBooleanChange={handleBooleanFilterChange}
                                                listId="ShowProfilesList"
                                                tooltip="Filter entrepreneurs by founder status, mentoring and unicorns."
                                                part="both"
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section>
                <div className="max-1140 mx-auto mb-3">
                    <div className="row">
                        <div className="filterresult border-0 mb-0">
                            <ul>
                                {appliedFilterChips}
                                {showProfileFilterChips}
                                {hasAppliedFilters && (
                                    <li className="clear">
                                        <a className="btn-clear" role="button" onClick={(e) => handleClearAllFilterClick(e, state, dispatch)}>
                                            Clear All
                                        </a>
                                    </li>
                                )}
                            </ul>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

export default EntrepreneurSearchControl;
